import React, { useState, useEffect, useRef } from "react";
import { useAccreditationCount } from "./hooks/useAccreditationCount";
import { Box, Button } from "@mui/material";
import ClassifierHeader from "./header/Header";
import Accreditation from "./classification_component/Accreditation";
import FileListArea from "./staging_area/FileListArea";
import ClassificationResultDialog from "./classification_component/ClassificationResultDialog";
import BatchPredictionSummaryDialog from "./classification_component/BatchPredictionSummaryDialog";
import MultiPageClassificationResultDialog from "./classification_component/MultiPageClassificationResultDialog";
import UtilityButtons from "./classification_component/UtilityButtons";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import ProcessingDialog from "./classification_component/ProcessingDialog";
import { saveClassificationResult } from "./utils/saveClassificationResult";
import { fetchAccreditationData } from "./utils/fetchAccreditationData";
import SyncingDialog from "./classification_component/SyncingDialog";
import { updateIsPredictedMetadata } from "../../../services/StorageServices";
import { getUserField } from "../../../shared/services/fetchUserField";

import {
  handleSinglePredict,
  handleBatchPredict,
  handleMultiPagePredict,
  handleUploadDocument,
  handleViewSizeChange,
  handleSelectAll,
  handleFileSelection,
} from "./utils/Handler";

import { syncCategoryFiles } from "../../../services/SyncService";
import { useFolder } from "../../../context/FolderContext";

function Main() {
  const { currentUser } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const [departmentId, setDepartmentId] = useState(null);
  const userId = currentUser ? currentUser.uid : null;

  useEffect(() => {
    async function fetchDepartment() {
      if (userId) {
        const dep = await getUserField(userId, "department");
        setDepartmentId(dep);
      }
    }
    fetchDepartment();
  }, [userId]);

  return (
    <MainContent
      currentUser={currentUser}
      navigate={navigate}
      location={location}
      userId={userId}
      departmentId={departmentId}
    />
  );
}

function MainContent({
  currentUser,
  navigate,
  location,
  userId,
  departmentId,
}) {
  const [loading, setLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const { folders, selectedFolder, setSelectedFolder } = useFolder();

  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState("syncing"); // 'syncing' | 'success' | 'error'
  const [syncMsg, setSyncMsg] = useState("");

  const [syncEnabled, setSyncEnabled] = useState(false);
  // Batch summary state
  const [showBatchSummary, setShowBatchSummary] = useState(false);
  const [batchCategoryCounts, setBatchCategoryCounts] = useState({});
  const [batchLowConfidenceCount, setBatchLowConfidenceCount] = useState(0);

  const [accreditationChangeCount, setAccreditationChangeCount] =
    useAccreditationCount(userId, selectedFolder);

  // Removed useUserFileStructure and useEffect that set selectedFolder from location.state

  useEffect(() => {
    async function loadFiles() {
      if (!userId) return;
      try {
        const { retrieveFilesAndUpdate } = await import("./utils/Handler");
        await retrieveFilesAndUpdate(
          setSinglePageFiles,
          setMultiPageFiles,
          setSelectedSinglePageFileIds,
          setSelectedMultiPageFileIds,
          setError
        );
      } catch (e) {
        setError("Failed to load files on mount: " + e.message);
      }
    }
    loadFiles();
  }, [userId]);

  useEffect(() => {
    async function checkSyncEnabled() {
      if (!userId || !selectedFolder) {
        setSyncEnabled(false);
        return;
      }
      try {
        const files = await fetchAccreditationData(
          userId,
          selectedFolder.folderId
        );
        setSyncEnabled(files.length > 0);
      } catch (e) {
        setSyncEnabled(false);
      }
    }
    checkSyncEnabled();
  }, [userId, selectedFolder, accreditationChangeCount]);

  const [singlePageFiles, setSinglePageFiles] = useState([]);
  const [multiPageFiles, setMultiPageFiles] = useState([]);

  const [selectedSinglePageFileIds, setSelectedSinglePageFileIds] = useState(
    []
  );
  const [selectedMultiPageFileIds, setSelectedMultiPageFileIds] = useState([]);
  const [selectAllSinglePage, setSelectAllSinglePage] = useState(false);

  const [showSingleResultDialog, setShowSingleResultDialog] = useState(false);
  const [singleClassificationResult, setSingleClassificationResult] =
    useState(null);
  const [multiPageClassificationResults, setMultiPageClassificationResults] =
    useState(null);
  const [showMultiPageResultDialog, setShowMultiPageResultDialog] =
    useState(false);
  const [
    selectedSingleClassificationFile,
    setSelectedSingleClassificationFile,
  ] = useState(null);

  // New state for batch classification results
  const [batchClassificationResults, setBatchClassificationResults] = useState(
    []
  );

  const handleSingleClassify = async (fileId) => {
    if (selectedSinglePageFileIds.length !== 1) {
      setError("Please select exactly one file for classification.");
      return;
    }
    const file = singlePageFiles.find((f) => f.id === fileId);
    if (!file) {
      setError("Selected file not found.");
      return;
    }
    setSelectedSingleClassificationFile(file);
    await handleSinglePredict(
      singlePageFiles,
      [fileId],
      setSingleClassificationResult,
      setLoading,
      setError
    );
    setShowSingleResultDialog(true);
  };

  const handleSyncChanges = async () => {
    if (!window.confirm("Sync changes?")) return;
    setSyncStatus("syncing");
    setSyncMsg("");
    setSyncing(true);
    try {
      await syncCategoryFiles(currentUser.uid, selectedFolder);
      setSyncStatus("success");
      setSyncMsg("Sync complete");
    } catch (err) {
      setSyncStatus("error");
      setSyncMsg(err.message || "Sync failed");
    }
  };

  const handleBatchClassify = async () => {
    await handleBatchPredict(
      singlePageFiles,
      selectedSinglePageFileIds,
      setBatchCategoryCounts,
      setBatchLowConfidenceCount,
      setBatchLoading,
      setError,
      setShowBatchSummary,
      setBatchClassificationResults
    );
  };

  const handleMultiPageClassify = async () => {
    if (selectedMultiPageFileIds.length !== 1) {
      setError("Please select exactly one multi-page file for classification.");
      return;
    }
    const file = multiPageFiles.find(
      (f) => f.id === selectedMultiPageFileIds[0]
    );
    if (!file) {
      setError("Selected multi-page file not found.");
      return;
    }
    await handleMultiPagePredict(
      multiPageFiles,
      [file.id],
      setMultiPageClassificationResults,
      setBatchLoading,
      setError
    );
    console.log("Setting showMultiPageResultDialog to true");
    setShowMultiPageResultDialog(true);
  };

  const handleFolderChange = (e) => {
    const fid = e.target.value;
    console.log("handleFolderChange called with folderId:", fid);
    const f = folders.find((x) => x.folderId === fid);
    console.log("Folder found for selection:", f);
    if (f) {
      setSelectedFolder(f);
    }
  };

  const handleRemoveClassifiedMultiPageFile = () => {
    if (!selectedMultiPageFileIds.length) return;
    const classifiedFileId = selectedMultiPageFileIds[0];
    setMultiPageFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== classifiedFileId)
    );
    setSelectedMultiPageFileIds((prevIds) =>
      prevIds.filter((id) => id !== classifiedFileId)
    );
    // Optionally, increment multiPageClassifyDone to trigger any reloads
    setAccreditationChangeCount((prev) => prev + 1);
    console.log("updating Accreditation.. [SUCCESS]");
  };

  return (
    <>
      <SyncingDialog
        open={syncing}
        status={syncStatus}
        message={syncMsg}
        onClose={() => setSyncing(false)}
      />
      <ProcessingDialog open={batchLoading} />
      <BatchPredictionSummaryDialog
        open={showBatchSummary}
        onClose={() => {
          setShowBatchSummary(false);
          setAccreditationChangeCount((prev) => prev + 1);
        }}
        categoryCounts={batchCategoryCounts}
        lowConfidenceCount={batchLowConfidenceCount}
        userId={userId}
        selectedFolder={selectedFolder}
        files={singlePageFiles.filter((file) =>
          selectedSinglePageFileIds.includes(file.id)
        )}
        classificationResults={batchClassificationResults}
        onSaveComplete={() => {
          // Remove saved files with confidence >= 0.7 from singlePageFiles and selectedSinglePageFileIds
          setSinglePageFiles((prevFiles) =>
            prevFiles.filter(
              (file) =>
                !selectedSinglePageFileIds.includes(file.id) ||
                (file.confidence !== undefined && file.confidence < 0.7)
            )
          );
          setSelectedSinglePageFileIds((prevIds) =>
            prevIds.filter(
              (id) =>
                !selectedSinglePageFileIds.includes(id) ||
                (batchClassificationResults.find((file) => file.id === id)
                  ?.confidence ?? 1) < 0.7
            )
          );

          setAccreditationChangeCount((prev) => prev + 1);
          setShowBatchSummary(false);
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <ClassifierHeader
          key={selectedFolder ? selectedFolder.folderId : "no-folder"}
          onBack={() => navigate("/dashboard")}
          folders={folders}
          selectedFolder={selectedFolder}
          onFolderChange={handleFolderChange}
          syncEnabled={syncEnabled}
          onSyncChanges={handleSyncChanges}
          syncing={syncing}
        />
        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          <Box sx={{ width: 250, flexShrink: 0, p: 2 }}>
            <Accreditation
              userId={userId}
              selectedFolder={selectedFolder}
              setError={setError}
              setAccreditationChangeCount={setAccreditationChangeCount}
              accreditationChangeCount={accreditationChangeCount}
            />
          </Box>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
            <FileListArea
              singlePageFiles={singlePageFiles}
              multiPageFiles={multiPageFiles}
              selectedSinglePageFileIds={selectedSinglePageFileIds}
              selectedMultiPageFileIds={selectedMultiPageFileIds}
              handleSinglePageFileSelection={(fid) => {
                const sel = handleFileSelection(
                  fid,
                  selectedSinglePageFileIds,
                  setSelectedSinglePageFileIds
                );
                console.log("Selected:", sel);
              }}
              handleMultiPageFileSelection={(fid) =>
                handleFileSelection(
                  fid,
                  selectedMultiPageFileIds,
                  setSelectedMultiPageFileIds
                )
              }
              selectAllSinglePage={selectAllSinglePage}
              handleSelectAllSinglePage={(e) =>
                handleSelectAll(
                  e,
                  singlePageFiles,
                  setSelectAllSinglePage,
                  setSelectedSinglePageFileIds
                )
              }
              //viewSizeSinglePage={viewSize}
              //viewSizeMultiPage={viewSize}
              handleViewSizeChangeSinglePage={(e, sz) =>
                handleViewSizeChange(e, sz, () => {})
              }
              handleViewSizeChangeMultiPage={(e, sz) =>
                handleViewSizeChange(e, sz, () => {})
              }
              loading={loading}
              error={error}
              success={success}
              setSinglePageFiles={setSinglePageFiles}
              setMultiPageFiles={setMultiPageFiles}
              setError={setError}
              setSuccess={setSuccess}
            />
          </Box>
        </Box>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100%",
            backgroundColor: "#fafafa",
            borderTop: "1px solid #e0e0e0",
            p: 2,
            display: "flex",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <UtilityButtons
            selectedSinglePageFileIds={selectedSinglePageFileIds}
            selectedMultiPageFileIds={selectedMultiPageFileIds}
            loading={loading}
            handleUploadDocument={() => {
              handleUploadDocument(
                setSinglePageFiles,
                singlePageFiles,
                setSelectedSinglePageFileIds,
                setMultiPageFiles,
                multiPageFiles,
                setSelectedMultiPageFileIds,
                setError,
                setSuccess,
                setLoading
              );
            }}
            handleSingleClassify={handleSingleClassify}
            handleBatchPredict={handleBatchClassify}
            handleMultiPageClassify={handleMultiPageClassify}
          />
        </Box>
        <ClassificationResultDialog
          open={showSingleResultDialog}
          result={singleClassificationResult}
          selectedSinglePageFileIds={selectedSinglePageFileIds}
          files={singlePageFiles}
          categories={null}
          onConfirmChoice={async (confirmedCategory) => {
            try {
              console.log("Confirm from Single Classify is invoked..");
              const file = singlePageFiles.find(
                (f) => f.id === selectedSinglePageFileIds[0]
              );
              setLoading(true);

              console.log("Selected folder before saving:", selectedFolder);

              // Check if re-assignment happened (confirmedCategory differs from original prediction)
              const originalCategory =
                singleClassificationResult.predicted_category ||
                singleClassificationResult.page?.predictions?.[0]?.category;

              if (confirmedCategory !== originalCategory) {
                // Import upload service dynamically to avoid circular dependencies
                const { uploadReassignedFile } = await import(
                  "../../../services/ReassignedFileUploadService"
                );
                console.log("-----------------------------------------------");
                console.log("[MAIN.JS][DEBUG][DEPARTMENTID][SINGLE-CLASSIFY]");
                console.log("Department ID: ", departmentId);
                // Uploading a copy of file to the training dataset
                await uploadReassignedFile(
                  file,
                  confirmedCategory,
                  departmentId
                );
              }

              console.log("Classification Result is being saved...");
              try {
                //Uploading to accreditation_folder, not yet synced
                await saveClassificationResult(
                  userId,
                  selectedFolder,
                  file,
                  singleClassificationResult,
                  confirmedCategory,
                  singleClassificationResult.summary || ""
                );
                console.log("Classification result saving [SUCCESS]");
              } catch (saveError) {
                console.error("Error saving classification result:", saveError);
                setError(
                  "Failed to save classification result: " + saveError.message
                );
                throw saveError;
              }

              console.log("Marking file as predicted...");
              // Hindi na siya makikita sa staging area, if predicted na
              await updateIsPredictedMetadata(file.storagePath, true);
              console.log("Marking file [SUCCESS]");
              // Remove the confirmed file from singlePageFiles
              setSinglePageFiles((prevFiles) =>
                prevFiles.filter((f) => f.id !== file.id)
              );
              // Remove the confirmed file ID from selectedSinglePageFileIds
              setSelectedSinglePageFileIds((prevIds) =>
                prevIds.filter((id) => id !== file.id)
              );

              //close na resultdialog for single
              setShowSingleResultDialog(false);

              // Increment single classify done counter to trigger reload
              // Pang refresh ng UI para di na makita ung na predict na
              setAccreditationChangeCount((prev) => prev + 1);
              console.log("Single Classify Confirm Process is done...");
            } catch (err) {
              console.error("Error in confirm choice handler:", err);
              setError("Failed to save classification result: " + err.message);
            } finally {
              setLoading(false);
            }
          }}
          onClose={() => setShowSingleResultDialog(false)}
        />
        <MultiPageClassificationResultDialog
          open={showMultiPageResultDialog}
          result={multiPageClassificationResults}
          files={multiPageFiles}
          userId={userId}
          selectedFolder={selectedFolder}
          selectedFile={
            multiPageFiles.find((f) => f.id === selectedMultiPageFileIds[0]) ||
            null
          }
          onClose={() => setShowMultiPageResultDialog(false)}
          updateAccreditation={handleRemoveClassifiedMultiPageFile}
          departmentId={departmentId}
        />
      </Box>
    </>
  );
}

export default Main;
